import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';
import { FaClock } from 'react-icons/fa';
import '../../scss/match/matchCard.scss';
import EditMatchModal from '../match/EditMatchModal';
import { createPortal } from 'react-dom';
import api from '../../services/apiService';
import { authService } from '../../services/authService';

const TrainingCard = ({ training, onTrainingDeleted, disableNavigation }) => {
    const navigate = useNavigate();
    const isAdminUser = authService.isAdmin();
    const isManager = authService.isManager();
    const isCoach = isManager || isAdminUser; // Coaches include managers and admins
    
    // Create full training datetime by combining date and time
    const trainingDateTime = new Date(`${training.match_date}T${training.match_time}`);
    const now = new Date();
    
    // Check if training hasn't started yet
    const isNotStarted = trainingDateTime > now;
    
    // Convert both dates to start of day for accurate comparison
    const trainingDate = new Date(training.match_date);
    trainingDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const isUpcoming = trainingDate >= today;
    const isFuture = trainingDate > today;

    const [showEditModal, setShowEditModal] = useState(false);
    const [localTotalYes, setLocalTotalYes] = useState(parseInt(training.total_yes) || 0);
    const [localTotalNo, setLocalTotalNo] = useState(parseInt(training.total_no) || 0);
    const [currentResponse, setCurrentResponse] = useState(training.response);

    // Use ref to track if component is mounted
    const mounted = useRef(true);
    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleResponse = async (e, response) => {
        e.stopPropagation(); // Prevent card click event

        if (currentResponse === response) {
            return; // Already selected
        }
        
        // Check if parent has permission to respond to matches for this child
        const playerId = sessionStorage.getItem('player_id');
        if (authService.isParent() && !authService.canRespondToMatches(playerId)) {
            console.error('No permission to respond to training');
            return;
        }

        try {
            // Update response in the database
            const response_data = await api.post(`${process.env.REACT_APP_API_URL}/update_player_response.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    player_id: playerId,
                    response: response,
                    match_id: training.match_id,
                    is_waitlist: 0  // No waitlist for training
                })
            });

            // If successful, update the UI
            if (response_data.data?.success) {
                if (mounted.current) {
                    // Update counts based on response
                    if (currentResponse === 'Yes' && response === 'no') {
                        setLocalTotalYes(prev => Math.max(0, prev - 1));
                        setLocalTotalNo(prev => prev + 1);
                    } else if (currentResponse === 'No' && response === 'yes') {
                        setLocalTotalNo(prev => Math.max(0, prev - 1));
                        setLocalTotalYes(prev => prev + 1);
                    } else if (currentResponse !== 'Yes' && response === 'yes') {
                        setLocalTotalYes(prev => prev + 1);
                    } else if (currentResponse !== 'No' && response === 'no') {
                        setLocalTotalNo(prev => prev + 1);
                    }
                    
                    // Update current response
                    setCurrentResponse(response.charAt(0).toUpperCase() + response.slice(1));
                }
            }
        } catch (error) {
            console.error('Error updating response:', error);
        }
    };

    const handleTrainingUpdated = (updatedTraining) => {
        // Implement any UI updates needed
        setShowEditModal(false);
    };

    const handleClickCard = () => {
        if (disableNavigation) return;
        navigate(`/training/${training.match_id}`);
    };

    // Format the training location and date
    const locationName = training.location || 'Training Ground';
    const dateParts = formatDate(training.match_date).split(' ');
    const dayOfWeek = dateParts[0].substring(0, 3); // Abbreviate day name to 3 chars
    const dayNumber = dateParts[1];
    const month = dateParts[2];
    const year = dateParts[3];
    
    // Format start and end time
    let startTime = '19:30';
    if (training.match_time) {
        // Extract time part and remove seconds if present
        startTime = training.match_time.includes(':') 
            ? training.match_time.split(':').slice(0, 2).join(':') 
            : training.match_time;
    }
    
    const durationMins = parseInt(training.duration) || 90;
    
    // Calculate end time
    const startTimeParts = startTime.split(':');
    const startHour = parseInt(startTimeParts[0]);
    const startMinute = parseInt(startTimeParts[1]);
    const totalEndMinutes = startHour * 60 + startMinute + durationMins;
    const endHour = Math.floor(totalEndMinutes / 60);
    const endMinute = totalEndMinutes % 60;
    const endTime = `${endHour}:${endMinute.toString().padStart(2, '0')}`;

    // Format for display
    const formattedTime = `${startTime} - ${endTime}`;

    return (
        <div 
            className={`match-card future-game training-card ${training.status} ${training.status && training.status !== 'active' ? 'has-status-overlay' : ''}`}
            onClick={handleClickCard}
        >
            {(training.status === 'cancelled' || training.status === 'postponed') && (
                <div className={`status-stamp ${training.status}`}>
                    {training.status === 'cancelled' ? 'Cancelled' : 
                     training.status === 'postponed' ? 'Postponed' : ''}
                </div>
            )}
            
            <div className="match-location">
                Training
                <div className="match-type">{locationName}</div>
            </div>
            
            <div className="match-date">
                {`${dayOfWeek} ${dayNumber} ${month} ${year}`}
            </div>
            
            <div className="match-time">
                <FaClock /> {formattedTime}
            </div>
            
            <div className="response-totals">
                <div className="total yes">
                    <div className="count">
                        <span className="current">{localTotalYes}</span>
                    </div>
                    <div className="label">Yes</div>
                </div>
                
                <div className="total no">
                    <div className="count">
                        <span className="current">{localTotalNo}</span>
                    </div>
                    <div className="label">No</div>
                </div>
            </div>
            
            {isUpcoming && !disableNavigation && !isCoach && 
             (!authService.isParent() || authService.canRespondToMatches(sessionStorage.getItem('player_id'))) && (
                <div className="response-section">
                    {/* Question text */}
                    <p>{authService.isParent() ? `Can ${sessionStorage.getItem('child_name')?.split(' ')[0] || 'your child'} attend training?` : 'Can you attend training?'}</p>
                    
                    <div className="response-buttons">
                        <button 
                            className={`response-btn yes ${currentResponse === 'Yes' ? 'active' : currentResponse === 'No' ? 'inactive' : ''} ${authService.isChild() ? 'disabled' : ''}`}
                            onClick={(e) => !authService.isChild() && handleResponse(e, 'yes')}
                            disabled={authService.isChild()}
                        >
                            Yes
                        </button>
                        <button 
                            className={`response-btn no ${currentResponse === 'No' ? 'active' : currentResponse === 'Yes' ? 'inactive' : ''} ${authService.isChild() ? 'disabled' : ''}`}
                            onClick={(e) => !authService.isChild() && handleResponse(e, 'no')}
                            disabled={authService.isChild()}
                        >
                            No
                        </button>
                    </div>
                    
                    {/* Show responder information */}
                    <div className="responder-info">
                        <small style={{ fontStyle: 'italic', color: '#666' }}>
                            {currentResponse && training.responder_first_name && training.responder_last_name ? (
                                <>
                                    Responded by {training.responder_first_name.charAt(0).toUpperCase() + training.responder_first_name.slice(1)} {training.responder_last_name.charAt(0).toUpperCase() + training.responder_last_name.slice(1)}
                                    {training.response_updated ? (
                                        <> on {new Date(training.response_updated).toLocaleDateString()} at {new Date(training.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                    ) : (
                                        <> on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                    )}
                                </>
                            ) : (
                                <>Awaiting response</>
                            )}
                        </small>
                    </div>
                </div>
            )}
            
            {showEditModal && (
                createPortal(
                    <EditMatchModal
                        match={{...training, type: 'training'}}
                        onClose={() => setShowEditModal(false)}
                        onMatchUpdated={handleTrainingUpdated}
                    />,
                    document.body
                )
            )}
        </div>
    );
};

export default TrainingCard; 